/**
 * Provides a hook to call a callback function periodically with the next interval.
 * 
 * Uses an array of time ranges ({@link IntervalJSON}) to determine the next interval.
 * 
 * @packageDocumentation
 * @module useRefreshIntervalFromIntervals
 * @public
 */

import type { IntervalJSON } from '@highimpact-frontend/api-types';
import { getStartAndEndDate } from '@highimpact/time-intervals';
import { useCallback, useEffect, useRef, useState } from 'react';

// -- type definitions --

/** The refresh interval in milliseconds. */
export type RefreshInterval = number;

/** The options for the hook. */
export interface UseRefreshIntervalFromIntervalsOptions {
  /** The array of time ranges as {@link IntervalJSON} to use to determine the next interval. */
  intervals: IntervalJSON[];
  /** The callback function to call with the next interval as an {@link IntervalJSON}. */
  onIntervalJSONChange: (interval: IntervalJSON) => void;
}

/** The result of the hook. */
export interface UseRefreshIntervalFromIntervalsResult {
  /** The refresh interval in milliseconds, or undefined if not set. */
  refreshInterval: RefreshInterval | undefined;
  /** A function to set the refresh interval in milliseconds. */
  setRefreshInterval: (interval: RefreshInterval | undefined) => void;
  /** A function to stop emitting intervals. */
  stopRefresh: () => void;
  /** A function to emit the next interval now as an {@link IntervalJSON}. */
  emitNextInterval: () => IntervalJSON | undefined;
  /** 
   * A function to set the next request interval start as an ISO string.
   * Passing `undefined` is equivalent to not calling this method at all.
   */
  setNextRequestIntervalStart: (startIso: string | undefined) => void;
  /** A function to read the next request interval start as an ISO string. */
  readNextIntervalStart: () => string | undefined;
}

// -- Helpers --

/** Resolve the nearest interval end date to the given now. */
function resolveNearestIntervalEndDate(
  intervals: IntervalJSON[],
  nowMs: number
): string | undefined {
  if (!intervals.length) {
    return undefined;
  }

  let bestEndDateIso: string | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const interval of intervals) {
    const [, enddate] = getStartAndEndDate(interval);
    const endMs = new Date(enddate).getTime();

    if (Number.isNaN(endMs)) {
      continue;
    }

    const distance = Math.abs(endMs - nowMs);
    const shouldReplace =
      distance < bestDistance ||
      (distance === bestDistance &&
        bestEndDateIso !== undefined &&
        endMs > new Date(bestEndDateIso).getTime());

    if (bestEndDateIso === undefined || shouldReplace) {
      bestEndDateIso = enddate;
      bestDistance = distance;
    }
  }

  return bestEndDateIso;
}

/** Build an {@link IntervalJSON} from a start and end ISO string. */
function buildRefreshInterval(startIso: string, endIso: string): IntervalJSON {
  return {
    type: 'fixed_time_range',
    name: null,
    startdate: startIso,
    enddate: endIso,
  };
}

// -- Hook --

/**
 * Hook to call a callback function periodically with the next interval.
 * 
 * @param options.intervals - The array of time ranges to use to determine the next interval.
 * @param options.onIntervalJSONChange - The callback function to call with the next interval.
 * @returns The result of the hook:
 * - `refreshInterval` - The refresh interval in milliseconds.
 * - `setRefreshInterval` - A function to set the refresh interval.
 * - `stopRefresh` - A function to stop the refresh.
 * - `emitNextInterval` - A function to emit the next interval.
 * - `setNextRequestIntervalStart` - A function to set the next request interval start.
 * - `readNextIntervalStart` - A function to read the next request interval start.
 */
export function useRefreshIntervalFromIntervals(
  options: UseRefreshIntervalFromIntervalsOptions
): UseRefreshIntervalFromIntervalsResult {
  const { intervals, onIntervalJSONChange } = options;

  const [refreshInterval, setRefreshInterval] = useState<
    RefreshInterval | undefined
  >(undefined);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalsRef = useRef<IntervalJSON[]>(intervals);
  const onIntervalJSONChangeRef = useRef(onIntervalJSONChange);
  const lastEndDateRef = useRef<string | undefined>(undefined);

  const clearActiveTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopRefresh = useCallback(() => {
    clearActiveTimer();
    setRefreshInterval(undefined);
  }, [clearActiveTimer]);

  const setNextRequestIntervalStart = useCallback(
    (startIso: string | undefined) => {
      lastEndDateRef.current =
        startIso ??
        resolveNearestIntervalEndDate(intervalsRef.current, Date.now());
    },
    []
  );

  const readNextIntervalStart = useCallback(() => {
    return lastEndDateRef.current;
  }, []);

  const emitNextInterval = useCallback((): IntervalJSON | undefined => {
    const nowIso = new Date().toISOString();

    const baselineEndDate =
      lastEndDateRef.current ??
      resolveNearestIntervalEndDate(
        intervalsRef.current,
        new Date(nowIso).getTime()
      );

    if (!baselineEndDate) {
      return undefined;
    }

    const startMs = new Date(baselineEndDate).getTime();
    const nowMs = new Date(nowIso).getTime();
    const safeEndIso = nowMs >= startMs ? nowIso : baselineEndDate;

    const nextInterval = buildRefreshInterval(baselineEndDate, safeEndIso);
    lastEndDateRef.current = safeEndIso;
    onIntervalJSONChangeRef.current(nextInterval);

    return nextInterval;
  }, []);

  useEffect(() => {
    intervalsRef.current = intervals;
    lastEndDateRef.current = resolveNearestIntervalEndDate(
      intervals,
      Date.now()
    );
  }, [intervals]);

  useEffect(() => {
    onIntervalJSONChangeRef.current = onIntervalJSONChange;
  }, [onIntervalJSONChange]);

  useEffect(() => {
    clearActiveTimer();

    if (!refreshInterval) {
      return () => {
        clearActiveTimer();
      };
    }

    emitNextInterval();

    timerRef.current = setInterval(() => {
      emitNextInterval();
    }, refreshInterval);

    return () => {
      clearActiveTimer();
    };
  }, [clearActiveTimer, emitNextInterval, refreshInterval]);

  return {
    refreshInterval,
    setRefreshInterval,
    stopRefresh,
    emitNextInterval,
    setNextRequestIntervalStart,
    readNextIntervalStart,
  };
}
